import { Flex, Box, useDisclosure, useToast } from '@chakra-ui/react';
import { Outlet, useLocation } from 'react-router-dom';
import { useEffect, useState, lazy, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';

import useNetworkTxnParam from '../../hooks/useNetworkTxnParam';
import useGameSounds from '../../hooks/useGameSounds';
import { INNERHEIGHT, MUTED } from '../../constants/localStorage';
import { getNameThroughAddress } from '../../constants/address';
import Header from '../../components/Header';
import { GAME_NETWORK_TO_CHAIN } from '../../constants/chain';
import { getJWT, onAuthenticate } from '../../utils/authenticate';

import { useSocket } from '../../socketContext';
import { useMobile } from '../../hooks/useMobile';
import { setIsMobile } from '../../slice/commonSlice';
import { getGameInfo } from '../../slice/gameSlice';
import useLocalStorageState from 'use-local-storage-state';
import {
  usePlayAccount,
  usePlayAddress,
  useRefreshPlayAddress,
} from '../../hooks/useAddress';
import { useModalContext } from '../../hooks/useModalContext';

const ConnectWalletModal = lazy(
  () => import('../../components/ConnectWalletModal')
);
const SwitchNetworkModal = lazy(() => import('./SwitchNetworkModal'));

const HIDE_CONNECT_WALLET_MODAL_PATH = new Set(['/', '/create-game']);

const Root = () => {
  const [containerStyles, setContainerStyles] = useState<Record<string, any>>({
    width: '100%',
    height: '100%',
  });
  const [isSocketConnected, setIsSocketConnected] = useState(false);

  const { address, isConnected } = usePlayAccount();
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { gameNetwork, gameContractAddress } = useNetworkTxnParam();
  const dispatch = useDispatch();
  const toast = useToast();
  const socket = useSocket();

  useRefreshPlayAddress();
  const { showSignMessageModal, hideSignMessageModal } = useModalContext();
  // 是否是移动端
  const { isMobile } = useMobile();
  const [innherHeight] = useLocalStorageState(INNERHEIGHT, {
    defaultValue: 0,
  });
  dispatch(setIsMobile(isMobile));

  useGameSounds();

  const isHomePage = location.pathname === '/';

  useEffect(() => {
    // @ts-ignore
    window.sigeMessage = {
      show: showSignMessageModal,
      hide: hideSignMessageModal,
    };
  }, []);

  useEffect(() => {
    socket?.on('connect', () => {
      setIsSocketConnected(true);
    });
    // Add error and connection handlers
    socket?.on('connect_error', error => {
      toast({
        status: 'error',
        title: 'Socket Connection Error',
        description: error?.message,
      });
      console.error('Connection error:', error);
    });
    socket?.on('reconnect', () => {
      setIsSocketConnected(true);
    });
    socket?.on('reconnect_failed', () => {
      console.error('Socket Reconnection Failed');
    });
    socket?.on('disconnect', () => {
      setIsSocketConnected(false);
    });

    return () => {
      socket?.off('connect');
      socket?.off('connect_error');
      socket?.off('reconnect');
      socket?.off('reconnect_failed');
      socket?.off('disconnect');
    };
  }, [socket]);

  useEffect(() => {
    const updateDatInRedux = (res: any) => {
      if (res?.gameInfo?.config) {
        if (
          !res.gameInfo.config.currency &&
          res.gameInfo.config.collateralToken
        ) {
          const gameChainId = GAME_NETWORK_TO_CHAIN[gameNetwork].id;
          res.gameInfo.config.currency =
            // @ts-ignore
            getNameThroughAddress(
              gameChainId,
              res.gameInfo.config.collateralToken
            );
        }
        console.log('info>>>', res);
        dispatch(getGameInfo(res));
      }
    };
    const connectSocket = async () => {
      try {
        console.log('connectSocket', address);
        await onAuthenticate(address!);
        const jwt = getJWT(address);
        if (jwt) {
          socket!.emit('chat data', {
            gameNetwork,
            gameContractAddress,
            token: jwt,
          });
          socket!.on('chat message', updateDatInRedux);
        }
      } catch (e: any) {
        toast({
          status: 'error',
          title: 'Failed to fetch game information',
          description: e?.message ?? e,
        });
        console.error(e);
      }
    };
    if (
      gameContractAddress &&
      gameNetwork &&
      address &&
      socket &&
      isSocketConnected
    ) {
      connectSocket();
    }
    return () => {
      socket?.off('chat message', updateDatInRedux);
    };
  }, [socket, isSocketConnected, gameNetwork, gameContractAddress, address]);

  // address不存在，不是homepage gamepage, 不存在currentAddress(未创建游戏或未坐下)，show connect wallet
  useEffect(() => {
    if (
      !isConnected &&
      !HIDE_CONNECT_WALLET_MODAL_PATH.has(location.pathname) &&
      !address
    ) {
      onOpen();
    } else {
      onClose();
    }
  }, [isConnected, location.pathname, onOpen, onClose, address]);

  useEffect(() => {
    if (isHomePage || isMobile) {
      setContainerStyles({
        width: '100%',
        height: '100%',
        minH: isMobile ? innherHeight : '', // 为移动端设置最小高度
      });
    } else {
      setContainerStyles({
        aspectRatio: 16 / 9,
        maxH: '100%',
        transform: 'translateY(-50%)',
        top: '50%',
      });
    }
  }, [isHomePage, isMobile]);

  useEffect(() => {
    return () => {
      localStorage.removeItem(MUTED);
    };
  }, [gameNetwork, gameContractAddress]);

  const h = useMemo(() => {
    if (isMobile) {
      if (innherHeight <= 610) {
        return '100vh';
      } else {
        return innherHeight;
      }
    }
    return '100vh';
  }, [isMobile, innherHeight]);

  const isGameSettingPage = location.pathname.includes('game-setting');
  // for mobile auto scroll to bottom
  const scrollRef = useRef<any>(null);
  useEffect(() => {
    const scrollToBottom = () => {
      if (scrollRef.current && isMobile) {
        // 延迟执行，确保内容完全渲染
        setTimeout(() => {
          scrollRef.current?.scrollTo({
            top: scrollRef.current.scrollHeight,
            behavior: 'smooth',
          });
        }, 100);
      }
    };
    isMobile && scrollToBottom();
  }, [isMobile]);
  return (
    <Box w="100vw" h={h} ref={scrollRef} overflow={isMobile ? 'auto' : ''}>
      <Flex
        flexDirection={'column'}
        maxW={'100%'}
        pos={'relative'}
        m="auto"
        {...containerStyles}
        overflow={isMobile ? 'auto' : ''}
      >
        <Header />
        <Box
          display={'flex'}
          justifyContent={'center'}
          flex={1}
          overflow={isGameSettingPage && isMobile ? 'scroll' : 'hidden'}
        >
          <Outlet />
        </Box>
        <ConnectWalletModal
          title="Welcome to Variance Poker"
          isOpen={isOpen}
          onClose={onClose}
        />
        {/* <SwitchNetworkModal isHomepage={isHomePage} gameNetwork={gameNetwork} /> */}
      </Flex>
    </Box>
  );
};

export default Root;
